import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useTheme } from "../theme";
import type { Session, Usage } from "../types";

/* The "a little, often" rhythm card from the marketing site, made real.
   Pure read-model: the weekly grid is derived from the sessions the dashboard
   already serves (each carries `started_at`/`ended_at`), and the monthly pacer
   comes straight from `/api/usage` (plans.quota_status). No new backend.

   Tone follows the product's "substance over streaks" stance: completed days
   get a quiet green check, never a broken-streak red. It encourages, it never
   guilts, and it never blocks — that's enforced elsewhere, not implied here. */

const DAY_MS = 86_400_000;
const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"]; // Monday-first, like the site

/** Local-calendar-day key, so days bucket by what the learner perceives, not UTC. */
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

function startOfWeek(today: Date): Date {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const offsetToMonday = (d.getDay() + 6) % 7; // getDay(): 0=Sun → Monday-first
  d.setDate(d.getDate() - offsetToMonday);
  return d;
}

interface WeekDay {
  label: string;
  done: boolean;
  today: boolean;
  future: boolean;
}

function buildWeek(sessions: Session[], now: Date): { days: WeekDay[]; doneCount: number } {
  const completedDays = new Set<string>();
  for (const s of sessions) {
    if (s.ended_at) completedDays.add(dayKey(new Date(s.started_at)));
  }
  const monday = startOfWeek(now);
  const todayKey = dayKey(now);
  const days: WeekDay[] = [];
  let doneCount = 0;
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
    const key = dayKey(d);
    const done = completedDays.has(key);
    if (done) doneCount++;
    days.push({ label: WEEKDAYS[i], done, today: key === todayKey, future: d.getTime() > now.getTime() && key !== todayKey });
  }
  return { days, doneCount };
}

/** A gentle, context-aware Italian line — the in-the-moment nudge. */
function nudge(sessions: Session[], now: Date): string {
  const completed = sessions.filter((s) => s.ended_at);
  if (completed.length === 0) return "Iniziamo? Bastano dieci minuti.";
  const todayKey = dayKey(now);
  if (completed.some((s) => dayKey(new Date(s.started_at)) === todayKey)) {
    return "Ottimo lavoro oggi. A domani, con calma.";
  }
  const last = Math.max(...completed.map((s) => new Date(s.started_at).getTime()));
  const daysSince = Math.floor((now.getTime() - last) / DAY_MS);
  if (daysSince >= 3) return "Bentornato! Riprendiamo con calma?";
  return "Pronti per i dieci minuti di oggi?";
}

function quotaCopy(u: Usage): { left: string; pct: number; note: string } {
  const pct = u.limit > 0 ? Math.min(100, Math.round((u.used / u.limit) * 100)) : 0;
  if (u.window === "trial") {
    return { left: `${u.remaining} of ${u.limit} left`, pct, note: "Free trial" };
  }
  const packNote = u.pack_credits ? ` +${u.pack_credits} pack` : "";
  let note: string;
  if (u.remaining <= 0 && u.pack_credits) note = "Monthly limit reached — using your session pack.";
  else if (u.remaining <= 0) note = "You've used this month's sessions. They reset next month.";
  else if (u.remaining <= 3) note = "A few left. Make them count.";
  else note = "Plenty left. Pace yourself, no need to cram.";
  return { left: `${u.remaining} left${packNote}`, pct, note };
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function TrendIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  );
}

export default function RhythmTracker() {
  const [theme] = useTheme();
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.getSessions(50).catch(() => [] as Session[]),
      api.getUsage().catch(() => null),
    ]).then(([s, u]) => {
      if (!alive) return;
      setSessions(s);
      setUsage(u);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Don't flash an empty shell — wait for the sessions read before painting.
  if (!sessions) return null;

  const now = new Date();
  const { days, doneCount } = buildWeek(sessions, now);
  const message = nudge(sessions, now);
  const q = usage ? quotaCopy(usage) : null;
  const portrait = theme === "light" ? "/elena-light3.png" : "/elena-dark3.png";

  return (
    <Link to="/calendar" className="card w-full max-w-sm mt-8 p-5 text-left flex flex-col gap-5 animate-rise hover:ring-1 hover:ring-border-soft transition-shadow cursor-pointer">
      {/* this week — encouragement, no broken-streak guilt */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="section-label">This week</span>
          <span className="inline-flex items-center gap-1 text-[0.72rem] text-success">
            {doneCount >= 3 ? (
              <>
                <TrendIcon />
                Nice rhythm
              </>
            ) : (
              "Adjust study plan →"
            )}
          </span>
        </div>
        <div className="flex gap-2">
          {days.map((d, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
              <span
                className={`w-full aspect-square rounded-[0.55rem] grid place-items-center ${
                  d.done ? "bg-success" : "bg-surface2"
                } ${d.today ? "border-2 border-success" : "border border-border-soft"} ${d.future ? "opacity-50" : ""}`}
              >
                {d.done && <CheckIcon />}
              </span>
              <span className={`text-[0.66rem] ${d.today ? "font-semibold text-success" : "text-text-tertiary"}`}>{d.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* sessions left — the pacer, framed as plenty, not a wall */}
      {q && usage && (
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-[0.85rem] text-text">{usage.window === "trial" ? "Trial sessions" : "Sessions this month"}</span>
            <span className="font-display font-semibold text-[0.95rem] text-ink">{q.left}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden bg-surface2">
            <div className="h-full rounded-full bg-success transition-[width] duration-500" style={{ width: `${q.pct}%` }} />
          </div>
          <p className="mt-2 text-[0.76rem] text-text-tertiary">{q.note}</p>
        </div>
      )}

      {/* gentle reminder nudge */}
      <div className="flex items-center gap-3 rounded-[0.7rem] px-3 py-2.5 bg-accent/10 border border-accent/20">
        <img src={portrait} alt="" draggable={false} className="w-8 h-8 rounded-full object-cover flex-shrink-0 select-none" style={{ objectPosition: "50% 30%" }} />
        <span className="text-[0.82rem] text-text leading-snug">{message}</span>
      </div>
    </Link>
  );
}
